import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Loader2,
  Search,
  Plus,
  Pencil,
  Copy,
  Trash2,
} from "lucide-react";
import { rimsList, rimsUpsert, rimsDelete, fmtInvestment } from "@/lib/api";
import {
  ActiveBadge,
  DeleteConfirm,
  TextField,
  NumberField,
  TextareaField,
  ArrayField,
  SwitchField,
  slugify,
  inputCls,
} from "./RimsKit";

const EMPTY_ROLE = {
  key: "",
  name: "",
  category: "",
  description: "",
  responsibilities: [],
  required_skills: [],
  preferred_experience: [],
  software: [],
  industries_served: [],
  training_period: "",
  search_tags: [],
  experience_tier: "",
  hourly_rate: "",
  monthly_rate: "",
  monthly_low: "",
  monthly_high: "",
  inhouse_monthly: "",
  currency: "USD",
  effective_date: "",
  status: "Active",
  active: true,
  order: null,
};

export const RolesManager = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState("create"); // create | edit | duplicate
  const [form, setForm] = useState(EMPTY_ROLE);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingKey, setTogglingKey] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await rimsList(token, "roles");
      setItems(data || []);
    } catch {
      toast.error("Failed to load roles");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const categories = useMemo(
    () => Array.from(new Set(items.map((r) => r.category).filter(Boolean))).sort(),
    [items]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((r) => {
      if (categoryFilter !== "all" && r.category !== categoryFilter) return false;
      if (activeFilter === "active" && !r.active) return false;
      if (activeFilter === "inactive" && r.active) return false;
      if (!q) return true;
      const hay = [
        r.name,
        r.key,
        r.category,
        ...(r.search_tags || []),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, categoryFilter, activeFilter]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const openCreate = () => {
    setMode("create");
    setForm({ ...EMPTY_ROLE });
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setMode("edit");
    setForm({ ...EMPTY_ROLE, ...role });
    setDialogOpen(true);
  };

  const openDuplicate = (role) => {
    setMode("duplicate");
    setForm({
      ...EMPTY_ROLE,
      ...role,
      key: "",
      name: `${role.name} (Copy)`,
      order: null,
    });
    setDialogOpen(true);
  };

  const handleToggleActive = async (role) => {
    setTogglingKey(role.key);
    // optimistic
    setItems((prev) =>
      prev.map((r) => (r.key === role.key ? { ...r, active: !r.active } : r))
    );
    try {
      await rimsUpsert(token, "roles", role.key, { ...role, active: !role.active });
      toast.success(`${role.name} ${!role.active ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Failed to update role");
      load();
    } finally {
      setTogglingKey("");
    }
  };

  const handleSave = async () => {
    const key = form.key?.trim() || slugify(form.name);
    if (!form.name?.trim()) return toast.error("Name is required");
    if (!key) return toast.error("Key is required");
    if (
      (mode === "create" || mode === "duplicate") &&
      items.some((r) => r.key === key)
    ) {
      return toast.error(`Key "${key}" already exists`);
    }
    setSaving(true);
    try {
      const payload = { ...form, key };
      // normalize numeric empties to null
      ["hourly_rate", "monthly_rate", "monthly_low", "monthly_high", "inhouse_monthly"].forEach(
        (k) => {
          if (payload[k] === "") payload[k] = null;
        }
      );
      await rimsUpsert(token, "roles", key, payload);
      toast.success(mode === "edit" ? "Role updated" : "Role created");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await rimsDelete(token, "roles", deleteTarget.key);
      toast.success("Role deleted");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete role");
    } finally {
      setDeleting(false);
    }
  };

  const keyDisabled = mode === "edit";

  return (
    <div data-testid="rims-roles-manager">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid="rims-roles-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search roles by name, category, tags..."
            className={`${inputCls} h-11 pl-9`}
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger data-testid="rims-roles-category-filter" className={`${inputCls} h-11 w-full sm:w-48`}>
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger data-testid="rims-roles-active-filter" className={`${inputCls} h-11 w-full sm:w-40`}>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Button
          data-testid="rims-add-role-button"
          onClick={openCreate}
          className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg h-11"
        >
          <Plus size={16} className="mr-1" /> Add Role
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/10 overflow-hidden" style={{ backgroundColor: "#0F1424" }}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#1E6BFF]" size={26} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/50" data-testid="rims-roles-empty">
            No roles found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/60 w-16">Active</TableHead>
                  <TableHead className="text-white/60">Role</TableHead>
                  <TableHead className="text-white/60 hidden md:table-cell">Category</TableHead>
                  <TableHead className="text-white/60">Monthly</TableHead>
                  <TableHead className="text-white/60 hidden lg:table-cell">Status</TableHead>
                  <TableHead className="text-white/60 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.key} data-testid="rims-role-row" className="border-white/10 hover:bg-white/5">
                    <TableCell>
                      <Switch
                        data-testid={`rims-role-toggle-${r.key}`}
                        checked={!!r.active}
                        disabled={togglingKey === r.key}
                        onCheckedChange={() => handleToggleActive(r)}
                        className="data-[state=checked]:bg-[#2EE59D]"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-white">{r.name}</div>
                      <div className="text-white/40 text-xs">{r.key}</div>
                    </TableCell>
                    <TableCell className="text-white/70 hidden md:table-cell">{r.category}</TableCell>
                    <TableCell className="text-white font-medium tabular-nums">
                      {fmtInvestment(r.monthly_low || r.monthly_rate, r.monthly_high || r.monthly_rate)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <ActiveBadge active={r.active} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`rims-edit-role-${r.key}`}
                          onClick={() => openEdit(r)}
                          className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10"
                        >
                          <Pencil size={15} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`rims-duplicate-role-${r.key}`}
                          onClick={() => openDuplicate(r)}
                          className="h-8 w-8 text-white/60 hover:text-[#66B2FF] hover:bg-white/10"
                        >
                          <Copy size={15} />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          data-testid={`rims-delete-role-${r.key}`}
                          onClick={() => setDeleteTarget(r)}
                          className="h-8 w-8 text-white/60 hover:text-[#FF5C7A] hover:bg-white/10"
                        >
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      <p className="text-white/40 text-xs mt-3">{filtered.length} role(s)</p>

      {/* Create / Edit / Duplicate dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-3xl bg-[#0F1424] border-white/10 text-white max-h-[90vh] overflow-y-auto"
          data-testid="rims-role-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {mode === "edit" ? "Edit Role" : mode === "duplicate" ? "Duplicate Role" : "Add Role"}
            </DialogTitle>
            <DialogDescription className="text-white/45">
              Define the role details, pricing, and metadata used by the recommendation engine.
            </DialogDescription>
          </DialogHeader>

          <div className="grid sm:grid-cols-2 gap-4 mt-1">
            <TextField
              label="Name *"
              testid="rims-role-name-input"
              value={form.name}
              onChange={(v) => set({ name: v })}
            />
            <TextField
              label="Key *"
              testid="rims-role-key-input"
              value={form.key}
              disabled={keyDisabled}
              hint={keyDisabled ? "Key cannot be changed after creation." : "Auto-generated from name if left blank."}
              onChange={(v) => set({ key: slugify(v) })}
            />
            <TextField
              label="Category"
              testid="rims-role-category-input"
              value={form.category}
              onChange={(v) => set({ category: v })}
            />
            <TextField
              label="Training Period"
              value={form.training_period}
              onChange={(v) => set({ training_period: v })}
            />
          </div>

          <TextareaField
            label="Description"
            testid="rims-role-description-input"
            value={form.description}
            onChange={(v) => set({ description: v })}
          />

          {/* Pricing */}
          <div className="mt-2 rounded-lg border border-white/10 p-4" style={{ backgroundColor: "#0A0F1E" }}>
            <div className="text-white/70 text-sm font-medium mb-3">Pricing (USD / month)</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <NumberField label="Monthly Rate" testid="rims-role-monthly-rate-input" value={form.monthly_rate} onChange={(v) => set({ monthly_rate: v })} />
              <NumberField label="Monthly Low" value={form.monthly_low} onChange={(v) => set({ monthly_low: v })} />
              <NumberField label="Monthly High" value={form.monthly_high} onChange={(v) => set({ monthly_high: v })} />
              <NumberField label="Hourly Rate" value={form.hourly_rate} onChange={(v) => set({ hourly_rate: v })} />
              <NumberField label="In-house Monthly" value={form.inhouse_monthly} onChange={(v) => set({ inhouse_monthly: v })} />
              <NumberField label="Display Order" value={form.order ?? ""} onChange={(v) => set({ order: v === "" ? null : v })} />
            </div>
          </div>

          {/* Arrays */}
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            <ArrayField label="Responsibilities" value={form.responsibilities} onChange={(v) => set({ responsibilities: v })} />
            <ArrayField label="Required Skills" value={form.required_skills} onChange={(v) => set({ required_skills: v })} />
            <ArrayField label="Preferred Experience" value={form.preferred_experience} onChange={(v) => set({ preferred_experience: v })} />
            <ArrayField label="Software" value={form.software} onChange={(v) => set({ software: v })} />
            <ArrayField label="Industries Served" value={form.industries_served} onChange={(v) => set({ industries_served: v })} />
            <ArrayField label="Search Tags" value={form.search_tags} onChange={(v) => set({ search_tags: v })} />
          </div>

          <div className="mt-3">
            <SwitchField
              label="Active (available to the recommendation engine)"
              testid="rims-role-active-switch"
              checked={form.active}
              onChange={(v) => set({ active: v })}
            />
          </div>

          <DialogFooter className="mt-2">
            <Button
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              className="text-white/70 hover:text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              data-testid="rims-role-save-button"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-lg"
            >
              {saving ? <Loader2 className="animate-spin mr-1" size={15} /> : null}
              {mode === "edit" ? "Save Changes" : "Create Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteConfirm
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete role?"
        description={`This will permanently remove "${deleteTarget?.name}" from the Role Intelligence Library. Future recommendations will no longer use it.`}
        onConfirm={handleDelete}
        busy={deleting}
        testid="rims-delete-role-dialog"
      />
    </div>
  );
};
